/**
 * Verification status helpers — shared between the Business Dashboard shell,
 * the Verification panel, and the Overview onboarding banner.
 *
 * Kept intentionally minimal. Do NOT import UI here.
 */

/* Statuses that are considered "operationally activated" — the tenant can
   publish products, activate services, attach vehicle listings, etc. */
export const VERIFIED_STATUS = "verified";

/* Statuses the business_verification_status field can hold on the biz doc. */
export const STATUS_META = {
  draft: {
    ar: "لم يُقدَّم الطلب بعد",
    en: "Not submitted",
    tone: "gray",
    description_ar: "لم يكتمل تسجيل نشاطك بعد. أكمل بيانات النشاط وارفع الوثائق المطلوبة، ثم أرسل الطلب لمراجعة إدارة كرتال.",
    description_en: "Your business is not registered yet. Complete the required data, upload the mandatory documents, and submit for CARTAL review.",
  },
  submitted: {
    ar: "قيد الاستلام",
    en: "Submitted",
    tone: "gold",
    description_ar: "تم استلام طلبك بنجاح. ستبدأ إدارة كرتال بمراجعته خلال 1–3 أيام عمل. سيتم تفعيل الوظائف التشغيلية بعد الموافقة.",
    description_en: "Your submission has been received. CARTAL admins will start the review within 1–3 business days.",
  },
  under_review: {
    ar: "قيد المراجعة",
    en: "Under review",
    tone: "gold",
    description_ar: "طلبك الآن قيد المراجعة من قِبل إدارة كرتال. لا يمكن نشر أو تفعيل المنتجات والخدمات أو ربط المركبات بالنشاط قبل الموافقة.",
    description_en: "Your submission is currently being reviewed. Publishing and activation are disabled until approval.",
  },
  needs_more_docs: {
    ar: "بحاجة إلى وثائق إضافية",
    en: "More documents required",
    tone: "orange",
    description_ar: "طلبت إدارة كرتال وثائق أو معلومات إضافية. راجع سبب الطلب بالأسفل، أرفع الوثائق المطلوبة، ثم أعِد إرسال الطلب.",
    description_en: "Admins requested additional documents. Review the note below, upload what's missing, and resubmit.",
  },
  approved: {
    ar: "موثَّق ومُعتمد",
    en: "Verified & approved",
    tone: "green",
    description_ar: "تم توثيق نشاطك بنجاح. جميع الوظائف التشغيلية مفعّلة الآن.",
    description_en: "Your business is verified. Operational features are now enabled.",
  },
  verified: {  // legacy value on business doc
    ar: "موثَّق ومُعتمد",
    en: "Verified & approved",
    tone: "green",
    description_ar: "تم توثيق نشاطك بنجاح. جميع الوظائف التشغيلية مفعّلة الآن.",
    description_en: "Your business is verified. Operational features are now enabled.",
  },
  rejected: {
    ar: "مرفوض",
    en: "Rejected",
    tone: "red",
    description_ar: "لم تتم الموافقة على طلبك. راجع سبب الرفض أدناه — يمكنك تعديل البيانات والوثائق وإعادة التقديم.",
    description_en: "Your submission was rejected. See the reason below — you can amend and resubmit.",
  },
  suspended: {
    ar: "موقوف مؤقتًا",
    en: "Suspended",
    tone: "red",
    description_ar: "تم إيقاف توثيق نشاطك مؤقتًا من قِبل الإدارة. تواصل مع الدعم لإعادة التفعيل.",
    description_en: "Your verification is temporarily suspended. Contact support to reactivate.",
  },
  revoked: {
    ar: "مسحوب",
    en: "Revoked",
    tone: "red",
    description_ar: "تم سحب التوثيق. تواصل مع الدعم لمعرفة الخطوات المتاحة.",
    description_en: "Your verification has been revoked. Contact support for next steps.",
  },
  needs_reverify: {
    ar: "بحاجة إلى تجديد التوثيق",
    en: "Reverification needed",
    tone: "orange",
    description_ar: "انتهت صلاحية إحدى وثائقك الرسمية. أعد رفع الوثيقة المنتهية، ثم أرسل الطلب لتجديد التوثيق.",
    description_en: "One of your official documents has expired. Re-upload it and resubmit for reverification.",
  },
  pending: {  // legacy value on business doc (set on submit)
    ar: "قيد الاستلام",
    en: "Submitted",
    tone: "gold",
    description_ar: "تم استلام طلبك. سيتم تفعيل الوظائف التشغيلية بعد موافقة إدارة كرتال.",
    description_en: "Submission received. Operational features unlock after CARTAL approval.",
  },
  unverified: {
    ar: "لم يُقدَّم الطلب بعد",
    en: "Not submitted",
    tone: "gray",
    description_ar: "لم يكتمل تسجيل نشاطك بعد. أكمل بيانات النشاط وارفع الوثائق المطلوبة، ثم أرسل الطلب لمراجعة إدارة كرتال.",
    description_en: "Your business is not registered yet. Complete the required data, upload the mandatory documents, and submit for CARTAL review.",
  },
};

export const TONE_COLORS = {
  gray:   "#8a8a8a",
  gold:   "#c9a55c",
  green:  "#16a34a",
  orange: "#f97316",
  red:    "#dc2626",
};

/* One source of truth: is this business allowed to use operational
   features (publish, activate, link vehicles, etc.)? */
export function isBusinessVerified(home) {
  const s = home?.verification?.business_verification_status;
  return s === VERIFIED_STATUS;
}

/* Sections that stay accessible before verification. Everything else is
   hard-gated (hidden from sidebar + redirects to /verification). */
export const ONBOARDING_SECTIONS = new Set(["overview", "verification", "profile"]);

/* Required documents to submit (backend enforces commercial_registration).
   The rest are recommended and appear in the checklist as "optional but
   strongly encouraged". */
export const REQUIRED_DOCS = ["commercial_registration"];

/* Per-category required documents on top of the baseline
   (commercial_registration). Introduced iter145 for the dealer
   category which must also upload the National Address Proof. */
export const CATEGORY_REQUIRED_DOCS = {
  dealer: ["national_address_proof"],   // معارض السيارات
};

export const RECOMMENDED_DOCS = [
  "national_business_number",
  "vat_certificate",
  "business_license",
  "authorized_rep_id",
  "authorization_letter",
];

export const DOC_LABELS = {
  commercial_registration:  { ar: "السجل التجاري",             en: "Commercial Registration" },
  national_business_number: { ar: "الرقم الوطني الموحّد",       en: "National Unified Number" },
  vat_certificate:          { ar: "شهادة ضريبة القيمة المضافة", en: "VAT Certificate" },
  business_license:         { ar: "الرخصة البلدية/التشغيلية",    en: "Business License" },
  national_address_proof:   { ar: "إثبات العنوان الوطني",         en: "National Address Proof" },
  authorized_rep_id:        { ar: "هوية الممثل المفوض",           en: "Authorized Rep ID" },
  authorization_letter:     { ar: "خطاب تفويض",                    en: "Authorization Letter" },
  iban_certificate:         { ar: "شهادة الآيبان",                  en: "IBAN Certificate" },
  other:                    { ar: "وثيقة أخرى",                     en: "Other document" },
};

/* Compute the checklist state given (business, verification submission,
   uploaded documents). Returns a list of steps with `done` booleans and a
   `nextStep` key indicating the first unfinished step. */
export function computeChecklist(business, submission, documents) {
  const contact = submission?.contact || {};
  const status = submission?.status || "draft";

  const step1 = Boolean(business?.name_ar && business?.category);
  const step2 = Boolean(contact.official_email && contact.official_phone);
  const uploadedTypes = new Set((documents || []).map((d) => d.doc_type));
  // Baseline required docs + any category-specific ones (e.g. dealer
  // requires national_address_proof in addition to the CR).
  const catExtras = CATEGORY_REQUIRED_DOCS[business?.category] || [];
  const allRequired = [...REQUIRED_DOCS, ...catExtras];
  const step3 = allRequired.every((t) => uploadedTypes.has(t));
  const step4 = ["submitted", "under_review", "needs_more_docs", "approved",
                 "rejected", "suspended", "revoked", "needs_reverify"].includes(status);
  const step5 = status === "approved";

  const steps = [
    { key: "business_info", done: step1, labelAr: "بيانات النشاط الأساسية", labelEn: "Business info" },
    { key: "contact",       done: step2, labelAr: "بيانات الاتصال الرسمية",  labelEn: "Official contact" },
    { key: "documents",     done: step3, labelAr: "المستندات المطلوبة",       labelEn: "Required documents" },
    { key: "submit",        done: step4, labelAr: "إرسال الطلب للمراجعة",    labelEn: "Submit for review" },
    { key: "approval",      done: step5, labelAr: "موافقة الإدارة",             labelEn: "Admin approval" },
  ];

  const nextStep = steps.find((s) => !s.done)?.key || "approval";
  const completed = steps.filter((s) => s.done).length;
  return { steps, nextStep, completed, total: steps.length };
}
